import { useCallback, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { cx } from '@libs/classnames';
import { FileUploadInput } from '@ui/components/FileUploadInput';
import { FormInput, FormLabel, FormRow } from '@ui/components/Form/Form';
import { Input } from '@ui/components/Input';
import { ThemeButton } from '@ui/components/ThemeButton';
import { ThemeLink } from '@ui/components/ThemeLink';
import { Tooltip } from '@ui/components/Tooltip/Tooltip';

import { Sponsor } from '@features/shared/api/typings';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings';

import styles from './TournamentEditFormInputs.module.css';

const MAX_TOURNAMENT_SPONSORS = 1;

type Props = {
  onUploadSponsorLogo: (sponsorNumber: number, file: FormData) => Promise<unknown>;
};
export function TournamentSponsorsInput({ onUploadSponsorLogo }: Props) {
  const [newSponsorCaption, setNewSponsorCaption] = useState('');
  const [newSponsorLink, setNewSponsorLink] = useState('');
  const nameRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const {
    control,
    formState: { disabled },
  } = useFormContext<TournamentEditFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sponsors',
  });

  const onAdd = useCallback(() => {
    if (!newSponsorCaption) return;

    append({
      number: (fields[fields.length - 1]?.number || 0) + 1,
      caption: newSponsorCaption,
      linkUri: newSponsorLink,
    });
    setNewSponsorCaption('');
    setNewSponsorLink('');
    nameRef.current?.focus();
  }, [newSponsorCaption, newSponsorLink, append, fields]);

  return (
    <FormRow>
      <FormLabel>Спонсоры турнира</FormLabel>
      <FormInput className={styles.sponsorsValues}>
        {fields.map((field: Sponsor, i) => {
          const onUploadLogo = (formData: FormData) => {
            return onUploadSponsorLogo(field.number, formData);
          };

          return (
            <div key={field.number} className={styles.sponsorValue}>
              {field.linkUri ? (
                <Tooltip className={styles.sponsorCaption} text={field.linkUri}>
                  <ThemeLink className={styles.stretch} inNewTab href={field.linkUri}>
                    {field.caption}
                  </ThemeLink>
                </Tooltip>
              ) : (
                <div className={cx(styles.sponsorCaption, styles.stretch)}>{field.caption}</div>
              )}

              <FileUploadInput
                className={styles.sponsorUploadInput}
                description="2 MB"
                value={field.logoUri}
                onChange={onUploadLogo}
                showPreview
                disabled={disabled}
              />

              <ThemeButton
                className={styles.sponsorButton}
                onClick={() => remove(i)}
                disabled={disabled}
              >
                Удалить
              </ThemeButton>
            </div>
          );
        })}

        {fields.length < MAX_TOURNAMENT_SPONSORS && (
          <div className={styles.newSponsorForm}>
            <Input
              inputRef={nameRef}
              className={styles.stretch}
              invisible
              value={newSponsorCaption}
              onChange={setNewSponsorCaption}
              onEnter={onAdd}
              placeholder="ООО 'Газпром'"
              disabled={disabled}
              required
            />
            <Input
              className={styles.stretch}
              invisible
              value={newSponsorLink}
              onChange={setNewSponsorLink}
              onEnter={onAdd}
              placeholder="http://t.me/test"
              disabled={disabled}
            />
            <ThemeButton
              className={styles.sponsorButton}
              onClick={onAdd}
              disabled={disabled || !newSponsorCaption}
            >
              Добавить
            </ThemeButton>
          </div>
        )}
      </FormInput>
    </FormRow>
  );
}
